/* eslint-disable no-negated-condition */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import {
  Wrapper, Title, ChildWrapper,
  Header, ExpandedIcon, CollapsedIcon,
  Name, Category, Separator, Icons,
  RunIcon, ConfigIcon,
  InfoContainer
} from './styled';
import ExecutionParams from '../../../../../ExecutionParams';
import ToolParams from '../../../../../ToolParams';
import WorkspaceSelector from '../../../../../WorkspaceSelector';
import { runUserAgentTool, saveAndRun, setToolSelected } from 'store/Agents/actions';
import cloneDeep from 'lodash/cloneDeep';
import { selectTriggerAgent, selectTriggerAgentWs } from 'store/Agents/selectors';
import { openModal } from 'store/modals/actions';
import { MODAL_AGENT_TRIGGER_WARNING } from 'store/modals/modals';
import Tags from 'Screens/Automation/Agents/components/AgentDetail/components/TagsSection';
import { Logo } from 'Screens/Automation/Agents/components/CreateNewAgent/components/Tools/components/Card/styled';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_AGENT_RUN, FEATURE_AGENTS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';

const ToolContent = ({ isExpanded, tool, parameters_data, onChangeField, id, setToolToSave, canEdit }) => {
  useEffect(() => () => {
    setToolToSave(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ChildWrapper>
      <ExecutionParams currentTool={ tool } disabled={ !canEdit } />
      <ToolParams defaultParams={ parameters_data } currentTool={ tool } onChangeField={ onChangeField } disabled={ !canEdit } />
      <WorkspaceSelector currentTool={ tool } disabled={ !canEdit } />
      <Tags toolId={ id } isExpanded={ isExpanded } disabled={ !canEdit } />
    </ChildWrapper>
  );
};

const Tool = ({
  tool, setToolToSave, setTriggerWarning, toolExpanded, setToolExpanded
}) => {
  const dispatch = useDispatch();
  const id = get(tool, 'id', 0);
  const name = get(tool, 'name', '');
  const category = get(tool, 'category', '');
  const type = get(tool, 'tool', '');
  const agentSelected = useSelector(selectTriggerAgent);
  const executor = agentSelected?.executors?.find((t) => t.id === id);
  const parameters_data = get(executor, 'parameters_data', {});
  const status = get(agentSelected, 'status', '');
  const active = get(agentSelected, 'active', false);
  const addedWorkspaces = useSelector((state) => selectTriggerAgentWs(state, get(tool, 'id', 0)));
  const isExpanded = toolExpanded === id;
  const runAllowedByRole = useSelector((state) => selectAllowedByRole(state, FEATURE_AGENT_RUN, PERMISSION_TYPES.CREATE));
  const saveAllowedByRole = useSelector((state) => selectAllowedByRole(state, FEATURE_AGENTS, PERMISSION_TYPES.PATCH));

  useEffect(() => {
    setToolToSave(tool);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeField = (field, value, fieldType) => {
    const paramsDataOrig = tool.parameters_data || {};
    const paramsData = cloneDeep(paramsDataOrig);
    if (value === '') delete paramsData[field];
    else { paramsData[field] = value; }
    if (fieldType === 'password') {
      setToolToSave(prev => ({ ...prev, parameters_data: paramsData }));
    } else {
      setToolToSave({ ...tool, parameters_data: paramsData });
    }
  };

  const handleClick = () => {
    if (isExpanded) {
      setToolExpanded(null);
      setToolToSave(null);
    } else {
      setToolExpanded(id);
      dispatch(setToolSelected(executor));
    }
  };

  const onClickConfig = (e) => {
    e.stopPropagation();
    if (!isExpanded) handleClick();
  };

  const mandatoryParamsFilled = () => {
    const mandatoryParams = tool?.parameters_metadata ? Object.keys(tool.parameters_metadata).filter((toolName) => tool.parameters_metadata[toolName].mandatory) : [];
    return mandatoryParams.every((toolName) => !isEmpty(get(tool, `parameters_data.${toolName}`, ''))) && !isEmpty(addedWorkspaces);
  };

  const run = (e) => {
    e.stopPropagation();
    if (status !== 'online') {
      setTriggerWarning('offline');
      dispatch(openModal(MODAL_AGENT_TRIGGER_WARNING));
    } else if (!active) {
      setTriggerWarning('blocked');
      dispatch(openModal(MODAL_AGENT_TRIGGER_WARNING));
    } else if (!mandatoryParamsFilled()) {
      setTriggerWarning('notConfigured');
      dispatch(openModal(MODAL_AGENT_TRIGGER_WARNING));
    } else {
      if (tool?.parameters_data && Object.keys(tool?.parameters_data).some(key => tool?.parameters_data?.[key] !== tool.parameters_data?.[key])) {
        if (runAllowedByRole && saveAllowedByRole) dispatch(saveAndRun(id, parameters_data, name));
      } else {
        if (runAllowedByRole) dispatch(runUserAgentTool(name, parameters_data, id));
      }
    }
  };

  const configured = () => {
    const mandatoryParams = tool?.parameters_metadata
      ? Object.keys(tool.parameters_metadata).filter((param) => tool.parameters_metadata[param].mandatory)
      : [];

    const allParamsValid = mandatoryParams.every((param) => {
      const val = get(tool, `parameters_data.${param}`);

      if (val === '' || val == null) return false;
      if (Array.isArray(val)) return val.length > 0 && val.some(item => typeof item === 'string' ? item.trim() !== '' : true);
      return true;
    });

    const workspacesValid = Array.isArray(addedWorkspaces) && addedWorkspaces.length > 0 && addedWorkspaces.some(ws => typeof ws === 'string' ? ws.trim() !== '' : true);

    return allParamsValid && workspacesValid;
  };

  return (
    <Wrapper>
      <Header onClick={ handleClick }>
        <Title>
          <Logo type={ type } small />
          <InfoContainer>
            <Name children={ name } />
            <Category children={ category } />
          </InfoContainer>
        </Title>
        <Separator />
        <Icons>
          <ConfigIcon configured={ configured() } onClick={ onClickConfig } />
          <RunIcon onClick={ run } />
        </Icons>
        { isExpanded ? <ExpandedIcon /> : <CollapsedIcon /> }
      </Header>
      { isExpanded && <ToolContent isExpanded={ isExpanded } tool={ tool } parameters_data={ parameters_data } onChangeField={ onChangeField } id={ id } setToolToSave={ setToolToSave } canEdit={ saveAllowedByRole } />}
    </Wrapper>
  );
};

export default React.memo(Tool);
